import { apiProxyRequest } from "Api/apiProxyRequest";

import type { ResourceWithLinks } from "Api/utils";

/**
 * Resolves every `${prefix}${n}` HAL link on a resource and merges the
 * fulfilled `{ [id]: T }` pages into a single id-keyed map.
 *
 * Mirrors how billing users resolve their `ref:users:N` links: a single
 * failing page is skipped rather than dropping the whole set, and `null`
 * entries (the ref endpoints can return them, e.g. for deleted projects) are
 * filtered out.
 */
export const resolveRefLinks = async <T>(
  resource: ResourceWithLinks,
  prefix: string
): Promise<Record<string, T>> => {
  const hrefs = Object.keys(resource._links)
    .filter(key => key.startsWith(prefix))
    .map(key => resource._links[key].href);

  const results = await Promise.allSettled(
    hrefs.map(href => apiProxyRequest<Record<string, T | null>>(href))
  );

  return results.reduce<Record<string, T>>((acc, result) => {
    if (result.status !== "fulfilled") return acc;
    for (const [id, value] of Object.entries(result.value)) {
      if (value !== null) acc[id] = value;
    }
    return acc;
  }, {});
};
